import Vue from 'vue'
import JsonApiObject from '@/_models/JsonApi'

import store from '@/store'
import Verify from '@/views/Authentication/dialog/verify/verify.vue'
import User from '@/views/User/models/user'
import router from '@/_router'
import { getRequest } from '@/plugins/api-axios'

export default class Authentication extends JsonApiObject {
    static allowCompromised = false
    static minLength = 1
    static minStrength = 0

    public constructor(data: any) {
        super(data)
    }

    static async getCurrentSession(impersonating = false) {
        let query = {
            filters: {
                'filter[isCurrent]': true,
            },
            include: ['impersonating'],
        }

        let [res] = await Promise.all([
            Vue.prototype.$vbit.get('authentication', 'sessions', query),
        ])

        let session = res.data.length > 0 ? res.data[0] : null

        if (!impersonating && session) {
            await User.getCurrentUser()
        }

        if (!impersonating) {
            store.commit('authenticated', session ? true : false)
            store.commit('impersonating', res.included.length > 0 ? res.included[0] : null)
        }

        return session ? session.id : null
    }

    static async postSession(data: any) {
        let query = {
            body: { data: data },
            useDetailedSignInErrors: true,
        }

        let emailToAdd = null
        if (store.getters.mobileExistsOnOtherUser) {
            emailToAdd = store.getters.inputEmail
            store.commit('emailExistsOnOtherUser', null)
            store.commit('mobileExistsOnOtherUser', null)
            store.commit('existOnOtherUserEmailVerification', true)
        }

        let res = await Vue.prototype.$vbit
            .post('authentication', `sessions`, query)
            .catch(async (error) => {
                if (error.code == 'proofRequired') {
                    let confirm = await JsonApiObject.openDialog(Verify, {
                        claim: error.claim,
                        type: 'mfa',
                    })

                    if (confirm) {
                        return await this.postSession(data)
                    }
                } else {
                    return error.code
                }
            })

        if (res.data) {
            let user = await User.getCurrentUser()
            if (emailToAdd) {
                await User.postSecondaryEmail(user.id, emailToAdd)
                router.push({ name: 'contact-settings' })
            }
            store.commit('authenticated', true)
            store.commit('impersonating', null)
        }

        return res
    }

    static async deleteSession() {
        let id = await this.getCurrentSession()

        await Vue.prototype.$vbit.delete('authentication', `sessions/${id}`)

        store.commit('authenticated', false)

        store.commit('impersonating', null)
    }

    static async passwordReset(username: string) {
        let query = {
            body: {
                data: {
                    type: 'passwordReset',
                    attributes: {
                        username: username,
                    },
                },
            },
        }

        let res = await Vue.prototype.$vbit
            .post('authentication', `passwordResets`, query)
            .catch((error) => {
                return error
            })

        return res
    }

    static async changePassword(data: any) {
        let query = {
            body: { data: data },
        }

        let res = await Vue.prototype.$vbit.patch('authentication', `users/${data.id}`, query)

        return res
    }

    static async impersonate(userId: string | null) {
        let id = await this.getCurrentSession(true)

        if (!id) return

        let query = {
            body: {
                data: {
                    type: 'session',
                    id: id,
                    relationships: {
                        impersonating: {
                            data: userId ? { id: userId, type: 'user' } : null,
                        },
                    },
                },
            },
            include: ['impersonating'],
        }

        let res = await Vue.prototype.$vbit.patch('authentication', `sessions/${id}`, query)

        if (res.data) {
            await User.getCurrentUser()
        }

        store.commit('authenticated', res.data)
        store.commit('impersonating', res.included.length > 0 ? res.included[0] : null)

        return res.data
    }

    static async postJWT() {
        let res = await Vue.prototype.$vbit.post(
            'authentication',
            'jwt?aud=https%3A%2F%2Fv2.byggekortleser.no'
        )
        return res.data
    }

    static async getBklrProjects() {
        store.commit('bklrLoading', true)
        let projects = {}
        let jwt = await this.postJWT()

        if (jwt) {
            let url = 'https://internal.byggekortleser.no/api/reportuser/vbit/projects'

            try {
                let res = await getRequest(url, {
                    Authorization: `Bearer ${jwt}`,
                    'Content-Type': 'application/json',
                })
                if (res.data) {
                    res.data.data.projects.map((item: any) => {
                        projects['' + item.id] = item
                    })
                    res.data.data.projects_id06.map((item: any) => {
                        projects['' + item.id] = item
                    })
                    store.commit('bklrProjects', projects)
                    store.commit('bklrProjectsFull', [
                        ...res.data.data.projects,
                        ...res.data.data.projects_id06,
                    ])

                    store.commit('bklrAll', res.data.data.sa)
                    store.commit('bklrID06All', res.data.data.sa_id06)
                } else {
                    // If bklr returns null, empty relataded stores
                    store.commit('bklrProjects', [])
                    store.commit('bklrProjectsFull', [])
                    store.commit('bklrAll', false)
                    store.commit('bklrID06All', false)
                }
            } catch (error) {
                //
            }
        }

        store.commit('bklrLoading', false)

        return projects
    }

    static async getPasswordRequirements() {
        let res = await Vue.prototype.$vbit.get('authentication', 'password-requirements')

        if ((res.status = 200)) {
            this.allowCompromised = res.data.allowCompromised
            this.minLength = res.data.minLength
            this.minStrength = res.data.minStrength
        }

        return res
    }

    static async checkPasswordStrength(password: string, user: string) {
        let query = {
            body: {
                password: password,
                userId: user,
            },
        }

        let res = await Vue.prototype.$vbit.post('authentication', 'password-strength', query)

        let response = {
            allowCompromised: this.allowCompromised,
            isCompromised: res.data.isCompromised,

            strength: this.checkStrengtht(res.data.strength),
            isWeak: this.minStrength > res.data.strength,

            minLength: this.minLength,
            short: this.minLength > password.length,

            valid:
                this.minStrength <= res.data.strength &&
                this.minLength <= password.length &&
                !(!this.allowCompromised && res.data.isCompromised),
        }

        return response
    }

    static checkStrengtht(val: number) {
        let strength = {
            color: 'error',
            progress: 10,
            text: 'fe_validation.password_very_weak',
        }

        if (val == 1) {
            strength = {
                color: 'error',
                progress: 25,
                text: 'fe_validation.password_very_weak',
            }
        }

        if (val == 2) {
            strength = {
                color: 'error',
                progress: 50,
                text: 'fe_validation.password_weak',
            }
        }

        if (val == 3) {
            strength = {
                color: 'primary',
                progress: 75,
                text: 'fe_validation.password_medium',
            }
        }

        if (val == 4) {
            strength = {
                color: 'success',
                progress: 100,
                text: 'fe_validation.password_strong',
            }
        }

        return strength
    }

    static async getUser(id: string) {
        let res = await Vue.prototype.$vbit.get('authentication', `users/${id}`)
        return res
    }

    static async deleteSessions(url: string | null) {
        if (url) {
            await Vue.prototype.$generalApi.post(url).catch(() => {
                //
            })
        }

        return
    }
}
